import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

// ─── Spinner ──────────────────────────────────────────────────────────────────
export const Spinner = ({ size = 'md', className = '', color }) => {
  const sizes = { xs: 'w-3 h-3 border', sm: 'w-4 h-4', md: 'w-7 h-7', lg: 'w-10 h-10 border-[3px]' };
  return (
    <span
      className={`spinner ${sizes[size]} ${className}`}
      style={color ? { color } : {}}
      role="status"
      aria-label="Loading"
    />
  );
};

// ─── Page Loader ──────────────────────────────────────────────────────────────
export const PageLoader = ({ message = 'Loading…' }) => (
  <div className="flex flex-col items-center justify-center min-h-[55vh] gap-3">
    <div className="relative">
      <Spinner size="lg" />
    </div>
    <p className="text-sm text-gray-400 font-medium animate-pulse">{message}</p>
  </div>
);

// ─── Skeleton / Loading placeholder ──────────────────────────────────────────
export const Skeleton = ({ className = '', lines = 1, height = 'h-4' }) => {
  if (lines === 1) return <div className={`skeleton ${height} rounded-md ${className}`} />;
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className={`skeleton ${height} rounded-md ${i === lines - 1 ? 'w-3/4' : 'w-full'}`} />
      ))}
    </div>
  );
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  open:        { bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-500',    label: 'Open' },
  in_progress: { bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500',   label: 'In Progress' },
  pending:     { bg: 'bg-orange-100',  text: 'text-orange-700',  dot: 'bg-orange-500',  label: 'Pending' },
  on_hold:     { bg: 'bg-slate-100',   text: 'text-slate-600',   dot: 'bg-slate-400',   label: 'On Hold' },
  resolved:    { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Resolved' },
  closed:      { bg: 'bg-gray-100',    text: 'text-gray-500',    dot: 'bg-gray-400',    label: 'Closed' },
  published:   { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Published' },
  draft:       { bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500',   label: 'Draft' },
  archived:    { bg: 'bg-gray-100',    text: 'text-gray-500',    dot: 'bg-gray-400',    label: 'Archived' },
  active:      { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Active' },
  inactive:    { bg: 'bg-gray-100',    text: 'text-gray-500',    dot: 'bg-gray-400',    label: 'Inactive' },
};

export const StatusBadge = React.memo(({ status, showDot = true }) => {
  const cfg = STATUS_CONFIG[status] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: status };
  return (
    <span className={`badge ${cfg.bg} ${cfg.text}`}>
      {showDot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />}
      {cfg.label}
    </span>
  );
});

// ─── Priority Badge ───────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  low:      { bg: 'bg-slate-100',  text: 'text-slate-600',  label: 'Low',      dot: 'bg-slate-400' },
  medium:   { bg: 'bg-sky-100',    text: 'text-sky-700',    label: 'Medium',   dot: 'bg-sky-500' },
  high:     { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High',     dot: 'bg-orange-500' },
  critical: { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Critical', dot: 'bg-red-500' },
};

export const PriorityBadge = React.memo(({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || { bg: 'bg-gray-100', text: 'text-gray-600', label: priority, dot: 'bg-gray-400' };
  return (
    <span className={`badge ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
});

// ─── Role Badge ───────────────────────────────────────────────────────────────
const ROLE_CONFIG = {
  superadmin: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'System Admin' },
  admin:      { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'IT Manager' },
  agent:      { bg: 'bg-cyan-100',   text: 'text-cyan-700',   label: 'IT Technician' },
  customer:   { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'End User' },
};

export const RoleBadge = ({ role }) => {
  const cfg = ROLE_CONFIG[role] || { bg: 'bg-gray-100', text: 'text-gray-600', label: role };
  return <span className={`badge ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>;
};

// ─── Avatar ───────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316'];

export const Avatar = ({ user, size = 'md' }) => {
  const sizes = {
    xs: 'w-6 h-6 text-[9px]',
    sm: 'w-7 h-7 text-[10px]',
    md: 'w-9 h-9 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-xl',
  };

  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || '?';
  const color = AVATAR_COLORS[(user?.firstName?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

  if (user?.avatar) {
    return (
      <img
        src={user.avatar}
        alt={user.firstName}
        className={`${sizes[size]} rounded-full object-cover flex-shrink-0 ring-2 ring-white`}
      />
    );
  }

  return (
    <div
      className={`${sizes[size]} rounded-full flex items-center justify-center font-bold flex-shrink-0 ring-2 ring-white select-none`}
      style={{ backgroundColor: color, color: '#fff' }}
    >
      {initials}
    </div>
  );
};

// ─── Avatar Group ─────────────────────────────────────────────────────────────
export const AvatarGroup = ({ users = [], max = 3 }) => {
  const shown = users.slice(0, max);
  const rest = users.length - max;
  return (
    <div className="flex -space-x-2">
      {shown.map((u) => <Avatar key={u._id} user={u} size="sm" />)}
      {rest > 0 && (
        <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
          +{rest}
        </div>
      )}
    </div>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
export const EmptyState = ({ icon: Icon, title, description, action, compact = false }) => (
  <div className={compact ? 'flex flex-col items-center py-10 px-4 text-center' : 'empty-state'}>
    {Icon && (
      <div className={compact ? 'w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-accent' : 'empty-state-icon'}>
        <Icon className={compact ? 'w-6 h-6 text-primary' : 'w-9 h-9 text-primary'} />
      </div>
    )}
    <h3 className={`font-semibold text-gray-800 mb-1 ${compact ? 'text-sm' : 'text-lg'}`}>{title}</h3>
    {description && <p className={`text-gray-500 mb-5 max-w-sm ${compact ? 'text-xs' : 'text-sm'}`}>{description}</p>}
    {action}
  </div>
);

// ─── Error / Alert Message ────────────────────────────────────────────────────
export const ErrorMessage = ({ message, onDismiss }) => (
  <div className="alert-error" role="alert">
    <XCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
    <span className="flex-1">{message}</span>
    {onDismiss && (
      <button onClick={onDismiss} className="flex-shrink-0 opacity-60 hover:opacity-100">
        <XCircleIcon className="w-4 h-4" />
      </button>
    )}
  </div>
);

export const SuccessMessage = ({ message }) => (
  <div className="alert-success" role="status">
    <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
    <span>{message}</span>
  </div>
);

export const InfoMessage = ({ message }) => (
  <div className="alert-info" role="status">
    <InformationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
    <span>{message}</span>
  </div>
);

export const WarningMessage = ({ message }) => (
  <div className="alert-warning" role="alert">
    <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
    <span>{message}</span>
  </div>
);

// ─── Stats Card ───────────────────────────────────────────────────────────────
const ICON_COLORS = {
  blue:   { bg: 'bg-blue-50',    text: 'text-blue-600' },
  green:  { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  orange: { bg: 'bg-orange-50',  text: 'text-orange-600' },
  red:    { bg: 'bg-red-50',     text: 'text-red-600' },
  purple: { bg: 'bg-purple-50',  text: 'text-purple-600' },
  primary:{ bg: 'bg-blue-50',    text: 'text-blue-600' },
  cyan:   { bg: 'bg-cyan-50',    text: 'text-cyan-600' },
};

export const StatsCard = React.memo(({ title, value, icon: Icon, color = 'primary', trend, trendUp, suffix = '' }) => {
  const c = ICON_COLORS[color] || ICON_COLORS.primary;
  return (
    <div className="metric-card">
      <div className={`metric-icon ${c.bg}`}>
        {Icon && <Icon className={`w-5 h-5 ${c.text}`} />}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium mb-0.5">{title}</p>
        <p className="text-2xl font-bold text-gray-900 leading-none">{value ?? '—'}{suffix}</p>
        {trend && (
          <p className={`text-xs mt-1 font-medium ${trendUp === true ? 'text-emerald-600' : trendUp === false ? 'text-red-500' : 'text-gray-400'}`}>
            {trendUp === true ? '↑' : trendUp === false ? '↓' : ''} {trend}
          </p>
        )}
      </div>
    </div>
  );
});

// ─── Pagination ───────────────────────────────────────────────────────────────
export const Pagination = ({ page, pages, onPageChange, total }) => {
  if (!pages || pages <= 1) return null;

  const range = Array.from({ length: Math.min(5, pages) }, (_, i) => {
    if (pages <= 5) return i + 1;
    if (page <= 3) return i + 1;
    if (page >= pages - 2) return pages - 4 + i;
    return page - 2 + i;
  });

  return (
    <div className="flex items-center justify-between gap-4 pt-2">
      {total != null && (
        <p className="text-sm text-gray-500 hidden sm:block">
          Page <span className="font-semibold text-gray-700">{page}</span> of <span className="font-semibold text-gray-700">{pages}</span>
          {total != null ? <> — <span className="font-semibold text-gray-700">{total}</span> total</> : ''}
        </p>
      )}
      <div className="flex items-center gap-1 ml-auto">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="page-btn"
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        {range.map((n) => (
          <button
            key={n}
            onClick={() => onPageChange(n)}
            className={`page-btn ${n === page ? 'active' : ''}`}
          >
            {n}
          </button>
        ))}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pages}
          className="page-btn"
          aria-label="Next page"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────
export const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel, loading = false, confirmLabel = 'Confirm', danger = true }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onCancel} role="dialog" aria-modal="true" aria-labelledby="confirm-modal-title">
      <div className="modal-panel animate-scale-in max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 id="confirm-modal-title" className="text-base font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="modal-body pt-3">
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        <div className="modal-footer">
          <button onClick={onCancel} disabled={loading} className="btn-secondary">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={danger ? 'btn-danger' : 'btn-primary'} aria-label={confirmLabel}>
            {loading ? <Spinner size="sm" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────
export const Modal = ({ isOpen, onClose, title, children, footer, size = 'md' }) => {
  if (!isOpen) return null;
  const widths = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-2xl' };
  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title' : undefined}>
      <div className={`modal-panel animate-scale-in ${widths[size]} w-full`} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="modal-header">
            <h3 id="modal-title" className="text-base font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="btn-icon btn-ghost" aria-label="Close dialog">
              <span className="text-gray-400 text-lg leading-none">×</span>
            </button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
export const TabBar = ({ tabs, active, onChange, variant = 'line' }) => {
  if (variant === 'pill') {
    return (
      <div className="tab-bar-pill" role="tablist">
        {tabs.map((t) => (
          <button key={t.id || t} onClick={() => onChange(t.id || t)} className={`tab-pill ${(t.id || t) === active ? 'active' : ''}`} role="tab" aria-selected={(t.id || t) === active}>
            {t.label || t}
            {t.count != null && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${(t.id || t) === active ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="tab-bar" role="tablist">
      {tabs.map((t) => (
        <button key={t.id || t} onClick={() => onChange(t.id || t)} className={`tab-btn ${(t.id || t) === active ? 'active' : ''}`} role="tab" aria-selected={(t.id || t) === active}>
          {t.label || t}
          {t.count != null && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

// ─── Section Header ───────────────────────────────────────────────────────────
export const SectionHeader = ({ title, subtitle, action, border = true }) => (
  <div className={`flex items-start justify-between gap-4 ${border ? 'pb-4 mb-5 border-b border-gray-100' : 'mb-4'}`}>
    <div>
      <h1 className="page-title">{title}</h1>
      {subtitle && <p className="page-subtitle">{subtitle}</p>}
    </div>
    {action && <div className="flex-shrink-0">{action}</div>}
  </div>
);

// ─── Toggle Switch ────────────────────────────────────────────────────────────
export const Toggle = ({ checked, onChange, label, description, disabled = false }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <div>
      {label && <p className="text-sm font-medium text-gray-800">{label}</p>}
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1 ring-primary ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{ backgroundColor: checked ? 'var(--color-primary)' : '#e2e8f0' }}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  </div>
);

// ─── Search Input ─────────────────────────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = 'Search…', className = '' }) => (
  <div className={`search-bar ${className}`} role="search">
    <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="input pl-9 bg-gray-50 border-gray-200"
      aria-label={placeholder}
    />
  </div>
);

// ─── Data Table ───────────────────────────────────────────────────────────────
export const DataTable = ({ columns, data, loading, emptyState, onRowClick, keyField = '_id' }) => {
  if (loading) {
    return (
      <div className="surface overflow-hidden" role="status" aria-label="Loading data">
        <table className="data-table">
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i}>
                {columns.map((_, j) => (
                  <td key={j} className="px-4 py-3"><div className="skeleton h-4 rounded" /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!data?.length) return emptyState || null;

  return (
    <div className="surface overflow-hidden">
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={{ width: col.width }}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr
                key={row[keyField]}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'cursor-pointer' : ''}
              >
                {columns.map((col) => (
                  <td key={col.key}>{col.render ? col.render(row[col.key], row) : row[col.key]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Star Rating ────────────────────────────────────────────────────────────
export const StarRating = ({ value = 0, onChange, max = 5, readOnly = false }) => (
  <div className="flex items-center gap-1">
    {Array.from({ length: max }).map((_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => !readOnly && onChange && onChange(i + 1)}
        disabled={readOnly}
        className={`text-yellow-400 transition-transform ${!readOnly ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}
      >
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill={i < value ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      </button>
    ))}
  </div>
);

// ─── Copy Button ──────────────────────────────────────────────────────────────
export const CopyButton = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={handleCopy} className="btn-ghost btn-sm text-xs" title="Copy to clipboard">
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
};

// ─── Search Highlight ─────────────────────────────────────────────────────────
export const HighlightText = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="search-highlight">{part}</mark>
          : part
      )}
    </>
  );
};

// ─── Date Picker ──────────────────────────────────────────────────────────────
export const DatePicker = ({ value, onChange, label, min, max, className = '' }) => (
  <div className={className}>
    {label && <label className="label">{label}</label>}
    <input
      type="date"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      min={min}
      max={max}
      className="input"
    />
  </div>
);

// ─── Date Range Picker ────────────────────────────────────────────────────────
export const DateRangePicker = ({ from, to, onFromChange, onToChange, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <DatePicker label="From" value={from} onChange={onFromChange} max={to} />
    <DatePicker label="To" value={to} onChange={onToChange} min={from} />
  </div>
);
